/**
 * Enhanced Toast Notification System
 * Integrates with error handling system for comprehensive user feedback
 */

import { createContext, useState, useContext, useCallback, useRef, memo } from 'react';
import EnhancedToast from '../components/ui/EnhancedToast';
import { ERROR_TYPES, ERROR_SEVERITY } from '../utils/errorHandling';

const EnhancedToastContext = createContext(null);

// Toast types with enhanced styling and behavior
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error', 
  WARNING: 'warning',
  INFO: 'info',
  LOADING: 'loading',
  ASSIGNMENT_SUCCESS: 'assignment_success',
  ASSIGNMENT_ERROR: 'assignment_error',
  NETWORK_ERROR: 'network_error'
};

// Toast positioning options
export const TOAST_POSITIONS = {
  TOP_RIGHT: 'top-right',
  TOP_LEFT: 'top-left',
  TOP_CENTER: 'top-center',
  BOTTOM_RIGHT: 'bottom-right',
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_CENTER: 'bottom-center'
};

// Default configurations for different toast types
const TOAST_CONFIGS = {
  [TOAST_TYPES.SUCCESS]: {
    duration: 4000,
    dismissible: true,
    showProgress: true
  },
  [TOAST_TYPES.ERROR]: {
    duration: 8000,
    dismissible: true,
    showProgress: true,
    persistent: false // Can be overridden for critical errors
  },
  [TOAST_TYPES.WARNING]: {
    duration: 6000,
    dismissible: true,
    showProgress: true
  },
  [TOAST_TYPES.INFO]: {
    duration: 4000,
    dismissible: true,
    showProgress: true
  },
  [TOAST_TYPES.LOADING]: {
    duration: 0, // Persistent until manually dismissed
    dismissible: false,
    showProgress: false,
    persistent: true
  },
  [TOAST_TYPES.ASSIGNMENT_SUCCESS]: {
    duration: 5000,
    dismissible: true,
    showProgress: true,
    showIcon: true
  },
  [TOAST_TYPES.ASSIGNMENT_ERROR]: {
    duration: 10000,
    dismissible: true,
    showProgress: true,
    showIcon: true,
    showActions: true
  },
  [TOAST_TYPES.NETWORK_ERROR]: {
    duration: 12000,
    dismissible: true,
    showProgress: true,
    showIcon: true,
    showActions: true,
    persistent: false
  }
};

// Memoized toast item component
const ToastItem = memo(({ toast, onRemove, onAction }) => {
  const handleClose = useCallback(() => {
    onRemove(toast.id);
  }, [toast.id, onRemove]);

  const handleAction = useCallback((actionType, actionData) => {
    onAction(toast.id, actionType, actionData);
  }, [toast.id, onAction]);

  return (
    <EnhancedToast
      {...toast}
      onClose={handleClose}
      onAction={handleAction}
    />
  );
});

ToastItem.displayName = 'ToastItem';

export function EnhancedToastProvider({ children, position = TOAST_POSITIONS.TOP_RIGHT, maxToasts = 5 }) {
  const [toasts, setToasts] = useState([]);
  const toastCounter = useRef(0);
  const activeToasts = useRef(new Map()); // Track active toasts for updates

  // Add a new toast with enhanced options
  const addToast = useCallback((options) => {
    const {
      message,
      type = TOAST_TYPES.INFO,
      title,
      description,
      duration,
      dismissible,
      showProgress,
      showIcon,
      showActions,
      actions = [],
      data = {},
      persistent = false,
      priority = 0 // Higher priority toasts show first
    } = options;

    // Get default config for toast type
    const config = TOAST_CONFIGS[type] || TOAST_CONFIGS[TOAST_TYPES.INFO];
    
    // Create unique toast
    const id = `${Date.now()}_${toastCounter.current++}`;
    const toast = {
      id,
      message,
      type,
      title,
      description,
      duration: duration !== undefined ? duration : config.duration,
      dismissible: dismissible !== undefined ? dismissible : config.dismissible,
      showProgress: showProgress !== undefined ? showProgress : config.showProgress,
      showIcon: showIcon !== undefined ? showIcon : config.showIcon,
      showActions: showActions !== undefined ? showActions : config.showActions,
      actions,
      data,
      persistent: persistent || config.persistent,
      priority,
      timestamp: Date.now()
    };

    // Add to active toasts tracking
    activeToasts.current.set(id, toast);

    setToasts(prevToasts => {
      // Add new toast and sort by priority (higher first)
      const newToasts = [...prevToasts, toast].sort((a, b) => b.priority - a.priority);
      
      // Limit number of visible toasts
      if (newToasts.length > maxToasts) {
        // Remove oldest low-priority toasts
        const keptToasts = [];
        const removedToasts = [];
        
        for (const toast of newToasts) {
          if (keptToasts.length < maxToasts) {
            keptToasts.push(toast);
          } else {
            removedToasts.push(toast);
          }
        }
        
        // Clean up removed toasts from tracking
        removedToasts.forEach(removedToast => {
          activeToasts.current.delete(removedToast.id);
        });
        
        return keptToasts;
      }
      
      return newToasts;
    });

    return id;
  }, [maxToasts]);

  // Remove a toast
  const removeToast = useCallback((id) => {
    activeToasts.current.delete(id);
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  // Update an existing toast
  const updateToast = useCallback((id, updates) => {
    const existingToast = activeToasts.current.get(id);
    if (!existingToast) return;

    const updatedToast = { ...existingToast, ...updates };
    activeToasts.current.set(id, updatedToast);

    setToasts(prevToasts => 
      prevToasts.map(toast => 
        toast.id === id ? updatedToast : toast
      )
    );
  }, []);

  // Handle toast actions (retry, dismiss, etc.)
  const handleToastAction = useCallback((toastId, actionType, actionData) => {
    const toast = activeToasts.current.get(toastId);
    if (!toast) return;

    switch (actionType) {
      case 'retry':
        if (toast.data.retryCallback) {
          toast.data.retryCallback();
        }
        removeToast(toastId);
        break;
        
      case 'dismiss':
        removeToast(toastId);
        break;
        
      case 'view_details':
        if (toast.data.detailsCallback) {
          toast.data.detailsCallback();
        }
        break;
        
      case 'contact_support':
        // Open support contact method
        window.open('mailto:support@oldmutual.com', '_blank');
        break;
        
      default:
        // Handle custom actions
        if (toast.data.customActions && toast.data.customActions[actionType]) {
          toast.data.customActions[actionType](actionData);
        }
    }
  }, [removeToast]);

  // Clear all toasts
  const clearAllToasts = useCallback(() => {
    activeToasts.current.clear();
    setToasts([]);
  }, []);

  // Enhanced convenience methods
  const showSuccess = useCallback((message, options = {}) => {
    return addToast({
      message,
      type: TOAST_TYPES.SUCCESS,
      title: options.title || 'Success',
      ...options
    });
  }, [addToast]);

  const showError = useCallback((error, options = {}) => {
    // Handle both string messages and AssignmentError objects
    const isAssignmentError = error?.type && error?.userMessage;
    
    const config = {
      type: TOAST_TYPES.ERROR,
      title: options.title || 'Error',
      message: isAssignmentError ? error.userMessage : (typeof error === 'string' ? error : error?.message || 'An error occurred'),
      ...options
    };

    // Add recovery actions for assignment errors
    if (isAssignmentError && error.recoveryActions) {
      config.actions = error.recoveryActions.map(action => ({
        label: action.label,
        type: action.action,
        delay: action.delay
      }));
      config.showActions = true;
      config.data = {
        errorType: error.type,
        errorSeverity: error.severity,
        retryCallback: options.onRetry,
        detailsCallback: options.onViewDetails,
        ...config.data
      };
    }

    return addToast(config);
  }, [addToast]);

  const showWarning = useCallback((message, options = {}) => {
    return addToast({
      message,
      type: TOAST_TYPES.WARNING,
      title: options.title || 'Warning',
      ...options
    });
  }, [addToast]);

  const showInfo = useCallback((message, options = {}) => {
    return addToast({
      message,
      type: TOAST_TYPES.INFO,
      title: options.title || 'Information',
      ...options
    });
  }, [addToast]);

  const showLoading = useCallback((message, options = {}) => {
    return addToast({
      message,
      type: TOAST_TYPES.LOADING,
      title: options.title || 'Loading',
      persistent: true,
      ...options
    });
  }, [addToast]);

  // Assignment-specific toast methods
  const showAssignmentSuccess = useCallback((claimId, message, options = {}) => {
    return addToast({
      message: message || `Claim #${claimId} assigned successfully`,
      type: TOAST_TYPES.ASSIGNMENT_SUCCESS,
      title: 'Claim Assigned',
      data: { claimId },
      ...options
    });
  }, [addToast]);

  const showAssignmentError = useCallback((claimId, error, options = {}) => {
    const isAssignmentError = error?.type && error?.userMessage;
    
    return addToast({
      message: isAssignmentError ? error.userMessage : (typeof error === 'string' ? error : error?.message || 'Assignment failed'),
      type: TOAST_TYPES.ASSIGNMENT_ERROR,
      title: 'Assignment Failed',
      showActions: true,
      actions: isAssignmentError ? error.recoveryActions : [
        { label: 'Try Again', type: 'retry' },
        { label: 'Contact Support', type: 'contact_support' }
      ],
      data: { 
        claimId,
        errorType: error?.type,
        errorSeverity: error?.severity,
        retryCallback: options.onRetry,
        ...options.data
      },
      ...options
    });
  }, [addToast]);

  const showNetworkError = useCallback((message, options = {}) => {
    return addToast({
      message: message || 'Network connection failed. Please check your internet connection.',
      type: TOAST_TYPES.NETWORK_ERROR,
      title: 'Connection Error',
      showActions: true,
      actions: [
        { label: 'Retry', type: 'retry' },
        { label: 'Refresh Page', type: 'refresh' }
      ],
      data: {
        retryCallback: options.onRetry,
        ...options.data
      },
      ...options
    });
  }, [addToast]);

  // Get position styles for toast container
  const getPositionStyles = useCallback(() => {
    const baseStyles = 'fixed z-50 pointer-events-none';
    
    switch (position) {
      case TOAST_POSITIONS.TOP_RIGHT:
        return `${baseStyles} top-4 right-4 flex flex-col items-end space-y-2`;
      case TOAST_POSITIONS.TOP_LEFT:
        return `${baseStyles} top-4 left-4 flex flex-col items-start space-y-2`;
      case TOAST_POSITIONS.TOP_CENTER:
        return `${baseStyles} top-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center space-y-2`;
      case TOAST_POSITIONS.BOTTOM_RIGHT:
        return `${baseStyles} bottom-4 right-4 flex flex-col-reverse items-end space-y-reverse space-y-2`;
      case TOAST_POSITIONS.BOTTOM_LEFT:
        return `${baseStyles} bottom-4 left-4 flex flex-col-reverse items-start space-y-reverse space-y-2`;
      case TOAST_POSITIONS.BOTTOM_CENTER:
        return `${baseStyles} bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col-reverse items-center space-y-reverse space-y-2`;
      default:
        return `${baseStyles} top-4 right-4 flex flex-col items-end space-y-2`;
    }
  }, [position]);

  const contextValue = {
    // Basic methods
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    
    // Assignment-specific methods
    showAssignmentSuccess,
    showAssignmentError,
    showNetworkError,
    
    // Management methods
    addToast,
    removeToast,
    updateToast,
    clearAllToasts,
    
    // State
    toasts,
    position
  };

  return (
    <EnhancedToastContext.Provider value={contextValue}>
      {children}
      <div className={getPositionStyles()}>
        {toasts.map(toast => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onRemove={removeToast}
            onAction={handleToastAction}
          />
        ))}
      </div>
    </EnhancedToastContext.Provider>
  );
}

export function useEnhancedToast() {
  const context = useContext(EnhancedToastContext);
  if (!context) {
    throw new Error('useEnhancedToast must be used within an EnhancedToastProvider');
  }
  return context;
}

// Backward compatibility with existing useToast
export function useToast() {
  try {
    return useEnhancedToast();
  } catch {
    // Fallback to basic toast context if enhanced is not available
    const basicContext = useContext(require('./ToastContext').ToastContext);
    if (!basicContext) {
      throw new Error('useToast must be used within a ToastProvider');
    }
    return basicContext;
  }
}

export default EnhancedToastProvider;